import 'react-image-crop/dist/ReactCrop.css';

import type { RefObject, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCrop, { type Crop } from 'react-image-crop';

import { Modal } from '../common/Modal';
import { useCropFlow } from './useCropFlow';
import type { NormalisedRect } from './webcam/faceFraming';

interface CropModalProps {
  src: string;
  /** Pre-positioned square from face detection; null falls back to a centred crop. */
  initialCrop: NormalisedRect | null;
  onCancel: () => void;
  /**
   * Take the cropped square. May return a promise, in which case the modal
   * stays up — busy, uncancellable — until it settles, and a rejection is shown
   * here with the Confirm button handed back. The kiosk uploads from this
   * callback so the blob never leaves the modal until it has landed: it exists
   * in exactly one place, and is discarded only on success.
   */
  onConfirm: (blob: Blob) => void | Promise<void>;
  /**
   * Insert a look-at-the-result step between cropping and `onConfirm`.
   *
   * The crop editor shows a rectangle over the source, which answers "did I
   * select the right square" but not "is this a good photo" — eyes open, in
   * focus, nobody's hand in the corner. Where `onConfirm` is the upload, as in
   * the kiosk, that is the last moment anyone can tell. `PhotoBoard` leaves
   * it off: its cropped result sits in the drop zone, re-croppable, until the
   * reader presses send, and a preview one dialog earlier would only be in
   * the way.
   */
  previewBeforeConfirm?: boolean;
}

function CropEditor({
  hidden,
  crop,
  onChange,
  imgRef,
  src,
  onImageLoad,
}: {
  hidden: boolean;
  crop: Crop | undefined;
  onChange: (crop: Crop) => void;
  imgRef: RefObject<HTMLImageElement | null>;
  src: string;
  onImageLoad: (e: SyntheticEvent<HTMLImageElement>) => void;
}) {
  const { t } = useTranslation();
  // The editor stays mounted behind the preview rather than being swapped
  // out. `imgRef` and the crop state have to survive a trip to the preview
  // and back — remounting the image would refire `onImageLoad` and reset
  // the square the operator had just adjusted.
  return (
    <div className="mb-5 flex max-h-[60vh] justify-center" hidden={hidden}>
      <ReactCrop
        crop={crop}
        onChange={(c: Crop) => onChange(c)}
        aspect={1}
        circularCrop
        minWidth={50}
      >
        <img
          ref={imgRef}
          src={src}
          alt={t('photoUpload.cropSourceAlt')}
          onLoad={onImageLoad}
          className="max-h-[55vh] max-w-full object-contain"
        />
      </ReactCrop>
    </div>
  );
}

function CropPreview({ url }: { url: string }) {
  const { t } = useTranslation();
  return (
    <div className="mb-5 flex flex-col items-center gap-2">
      <img
        src={url}
        alt={t('photoUpload.previewAlt')}
        className="max-h-[55vh] max-w-full border border-rule object-contain"
      />
      <p className="label text-muted">{t('photoUpload.previewHint')}</p>
    </div>
  );
}

function CropActions({
  previewing,
  previewBeforeConfirm,
  busy,
  sending,
  onBack,
  onDismiss,
  onSend,
  onCrop,
}: {
  previewing: boolean;
  previewBeforeConfirm: boolean;
  busy: boolean;
  sending: boolean;
  onBack: () => void;
  onDismiss: () => void;
  onSend: () => void;
  onCrop: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const cropLabel = previewBeforeConfirm
    ? t('photoUpload.previewCrop')
    : t('photoUpload.confirmCrop');
  const idleLabel = previewing ? t('photoUpload.sendPhoto') : cropLabel;
  const primaryLabel = sending ? t('photoUpload.sending') : idleLabel;
  return (
    <div className="flex gap-3">
      <button
        type="button"
        // react-doctor-disable-next-line react-doctor/no-autofocus -- in a modal <dialog>, autofocus is how the opening focus is chosen (the browser's dialog focusing steps)
        autoFocus
        onClick={previewing ? onBack : onDismiss}
        disabled={sending}
        className="btn-ghost flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-50"
      >
        {previewing ? t('photoUpload.recrop') : t('common.cancel')}
      </button>
      <button
        type="button"
        onClick={() => {
          if (previewing) {
            onSend();
          } else {
            void onCrop();
          }
        }}
        disabled={busy}
        className="btn-primary flex-1 justify-center disabled:cursor-not-allowed disabled:opacity-50"
      >
        {primaryLabel}
      </button>
    </div>
  );
}

export function CropModal({
  src,
  initialCrop,
  onCancel,
  onConfirm,
  previewBeforeConfirm = false,
}: CropModalProps) {
  const { t } = useTranslation();
  const {
    imgRef,
    crop,
    setCrop,
    error,
    busy,
    sending,
    preview,
    onImageLoad,
    dismiss,
    handleCrop,
    handleSend,
    backToCrop,
  } = useCropFlow({ initialCrop, onCancel, onConfirm, previewBeforeConfirm });

  const title = preview
    ? t('photoUpload.previewTitle')
    : t('photoUpload.cropTitle');

  return (
    <Modal
      aria-labelledby="crop-modal-title"
      onClose={dismiss}
      // Not while an upload is in flight: the request would land anyway and
      // the operator would be left with no sign of a photo they think they
      // cancelled. The Cancel button is disabled for the same reason. Note
      // `sending`, not `busy` — dismissing *during the crop* stays allowed.
      onCancel={() => {
        if (!sending) {
          dismiss();
        }
      }}
      className="backdrop:bg-ink/70"
    >
      <div className="w-full max-w-lg border border-rule bg-card p-6">
        <h3 id="crop-modal-title" className="display mb-4 text-xl text-ink">
          {title}
        </h3>
        <CropEditor
          hidden={preview !== null}
          crop={crop}
          onChange={setCrop}
          imgRef={imgRef}
          src={src}
          onImageLoad={onImageLoad}
        />
        {preview && <CropPreview url={preview.url} />}
        {error && (
          <p role="alert" className="mb-3 text-sm text-magenta">
            {error}
          </p>
        )}
        <CropActions
          previewing={preview !== null}
          previewBeforeConfirm={previewBeforeConfirm}
          busy={busy}
          sending={sending}
          onBack={backToCrop}
          onDismiss={dismiss}
          onSend={handleSend}
          onCrop={handleCrop}
        />
      </div>
    </Modal>
  );
}
